import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { GridColDef, DataGrid } from '@mui/x-data-grid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPenToSquare,
    faArrowUp,
    faArrowDown,
    faTrashCan,
} from '@fortawesome/free-solid-svg-icons';
import './SectionsTable.scss';
import { DocumentSection } from '../../../../slices/marketplace/timeline/models/document-section.model';
import { DocumentDetails } from '../../../../slices/marketplace/timeline/models/document-details.model';
import Switch from 'react-switch';
import { TimelineParty } from '../../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { ActionDocumentType } from '../../../../slices/marketplace/timeline/models/enums/action-document-type.enum';

interface SectionsTableProps {
    documentsSections: DocumentSection[];
    documentDetailsList: DocumentDetails[];
    handleSectionToUpdate: (id: string) => void;
    handleSectionToRemove: (id: string) => void;
    handleSectionMove: (id: string, direction: 'up' | 'down') => void;
    handleBuyersAvailability: (id: string, isAvailable: boolean) => void;
}

const SectionsTable = (props: SectionsTableProps) => {
    const [sectionDetails, setSectionDetails] = useState<
        { id: string; isUsed: boolean; isUsedForBuyer: boolean }[]
    >([]);

    useEffect(() => {
        const updatedDetails = props.documentsSections.map((section) => ({
            id: section.id,
            isUsed: props.documentDetailsList.some(
                (doc: DocumentDetails) => doc.sectionId === section.id
            ),
            isUsedForBuyer: props.documentDetailsList.some(
                (doc: DocumentDetails) =>
                    doc.sectionId === section.id &&
                    (doc.counterParty === TimelineParty.Buyer ||
                        (doc.counterParty == TimelineParty.VetValue &&
                            !doc.isEngagementLetter) ||
                        doc.engagementBuyerId ||
                        doc.type === ActionDocumentType.DocumentWithFeedback)
            ),
        }));
        setSectionDetails(updatedDetails);
    }, [props.documentsSections, props.documentDetailsList]);

    const columns: GridColDef[] = [
        {
            field: 'index',
            headerName: 'Place',
            flex: 2,
            minWidth: 50,
            editable: false,
            sortable: false,
            resizable: false,
            renderCell: (params: any) => {
                return (
                    <div>
                        {params.row.index}{' '}
                        <FontAwesomeIcon
                            icon={faArrowUp}
                            className={'arrow-icon'}
                            onClick={() => {
                                props.handleSectionMove(params.row.id, 'up');
                            }}
                        />{' '}
                        <FontAwesomeIcon
                            icon={faArrowDown}
                            className={'arrow-icon'}
                            onClick={() => {
                                props.handleSectionMove(params.row.id, 'down');
                            }}
                        />
                    </div>
                );
            },
        },
        {
            field: 'name',
            headerName: 'Name',
            flex: 2,
            minWidth: 100,
            editable: false,
            sortable: false,
            resizable: false,
        },
        {
            field: 'isInUse',
            headerName: 'Status',
            flex: 1,
            minWidth: 100,
            editable: false,
            sortable: false,
            resizable: false,
            renderCell: (params: any) => {
                const sectionStatus = sectionDetails.find(
                    (status) => status.id === params.row.id
                );
                const isUsed = sectionStatus?.isUsed ?? false;

                return (
                    <div
                        className={isUsed ? 'text-success' : 'text-danger'}
                        style={{ fontWeight: 700 }}>
                        {isUsed ? 'Used' : 'Unused'}
                    </div>
                );
            },
        },
        {
            field: 'isAvailableForBuyers',
            headerName: 'For Buyers',
            flex: 1,
            minWidth: 100,
            maxWidth: 110,
            editable: false,
            sortable: false,
            resizable: false,
            cellClassName: 'buyers-switch',
            renderCell: (params: any) => {
                const sectionStatus = sectionDetails.find(
                    (status) => status.id === params.row.id
                );
                return (
                    <Switch
                        offColor="#d92550"
                        checked={params.row.isAvailableForBuyers}
                        onChange={() => {
                            props.handleBuyersAvailability(
                                params.row.id,
                                !params.row.isAvailableForBuyers
                            );
                        }}
                        disabled={
                            params.row.isAvailableForBuyers && sectionStatus?.isUsedForBuyer
                        }
                        height={18}
                        handleDiameter={12}
                        width={50}
                    />
                );
            },
        },
        {
            field: 'action',
            headerName: 'Actions',
            flex: 0,
            minWidth: 70,
            resizable: false,
            sortable: false,
            renderCell: (params: any) => (
                <div className="action-cell">
                    <div className="d-flex justify-content-start align-items-center">
                        <FontAwesomeIcon
                            onClick={() => {
                                props.handleSectionToUpdate(params.row.id);
                            }}
                            className="cursor-pointer"
                            size="lg"
                            icon={faPenToSquare}
                        />
                        <FontAwesomeIcon
                            onClick={() => {
                                if (!params.row.isLoiSection) props.handleSectionToRemove(params.row.id);
                            }}
                            className={`${'ml-4 trash-icon'}${
                                params.row.isLoiSection ||
                                sectionDetails.find((status) => status.id === params.row.id)?.isUsed
                                    ? ' disabled-icon'
                                    : ' cursor-pointer'
                            }`}
                            size="lg"
                            icon={faTrashCan}
                        />
                    </div>
                </div>
            ),
        },
    ];

    const additionalStyles = {
        border: 'none',
        '&::-webkit-scrollbar': {
            display: 'none',
        },
        '.MuiDataGrid-scrollbar': {
            display: 'none',
        },
        '.MuiDataGrid-row': {
            borderBottom: 'none',
        },
        '.MuiDataGrid-columnHeader:focus': {
            outline: 'none',
        },
        '.MuiDataGrid-cell:focus': {
            outline: 'none',
        },
    };

    return (
        <Box
            sx={{ width: '100%', overflow: 'auto', scrollbarWidth: 'none' }}
            className="sections-table">
            <DataGrid
                disableColumnMenu
                disableColumnFilter
                disableColumnSelector
                disableColumnResize
                sx={additionalStyles}
                columns={columns}
                rows={props.documentsSections}
                loading={false}
            />
        </Box>
    );
};

export default SectionsTable;
